import { Spacings, Text } from '@styles'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

/* ---------- Styles ---------- */
const Wrap = styled.div`
	display: inline-flex;
	position: relative;
	padding: 4px;
	border-radius: ${Spacings.SPACING_2B};
	background: rgba(242, 244, 248, 0.7);
	box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06);
	gap: 4px;
	user-select: none;

	/* CSS vars controlled by JS */
	--slider-x: 0px;
	--slider-w: 0px;
`

const Item = styled.button`
	position: relative;
	z-index: 1; /* above slider */
	appearance: none;
	border: 0;
	background: transparent;
	padding: 10px 16px;
	border-radius: ${Spacings.SPACING_2B};
	font-size: ${Text.LARGE};
	line-height: 1;
	cursor: pointer;
	color: #667085;
	transition: color 150ms ease, font-weight 150ms ease;
	font-weight: 500;
	white-space: nowrap; /* keep text on one line */
	align-items: center;
	display: inline-flex;

	&[aria-checked='true'] {
		color: #101828;
		font-weight: 500;
	}

	&:focus-visible {
		outline: none;
	}
	&:disabled {
		cursor: not-allowed;
		opacity: 0.6;
	}
`

const Slider = styled.span`
	position: absolute;
	top: 4px;
	bottom: 4px;
	left: 0; /* fixed; we animate translateX */
	width: var(--slider-w);
	border-radius: ${Spacings.SPACING_2B};
	background: #ffffff;
	box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.12), 0 3px 1px 0 rgba(0, 0, 0, 0.04);
	transform: translateX(var(--slider-x));
	transition: transform 250ms ease, width 250ms ease;
	pointer-events: none;
`

/* ---------- Component ---------- */
export default function SegmentedSwitch({ options = [], value, onChange, className, style, disabled = false }) {
	const normalized = useMemo(() => options.map((o) => (typeof o === 'string' ? { label: o, value: o } : o)), [options])

	const activeIndex = Math.max(
		0,
		normalized.findIndex((o) => o.value === value)
	)

	const wrapRef = useRef(null)
	const btnRefs = useRef([])
	btnRefs.current = []

	const addBtnRef = (el) => {
		if (el && !btnRefs.current.includes(el)) btnRefs.current.push(el)
	}

	const [sliderRect, setSliderRect] = useState({ x: 0, w: 0 })

	const updateSlider = () => {
		const wrap = wrapRef.current
		const activeBtn = btnRefs.current[activeIndex]
		if (!wrap || !activeBtn) return
		const wrapBox = wrap.getBoundingClientRect()
		const btnBox = activeBtn.getBoundingClientRect()
		setSliderRect({ x: btnBox.left - wrapBox.left, w: btnBox.width })
	}

	useLayoutEffect(() => {
		updateSlider()
		const ro = new ResizeObserver(updateSlider)
		btnRefs.current.forEach((b) => b && ro.observe(b))
		if (wrapRef.current) ro.observe(wrapRef.current)
		window.addEventListener('resize', updateSlider)
		return () => {
			ro.disconnect()
			window.removeEventListener('resize', updateSlider)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeIndex, normalized.length])

	useEffect(() => {
		if (!wrapRef.current) return
		wrapRef.current.style.setProperty('--slider-x', `${sliderRect.x}px`)
		wrapRef.current.style.setProperty('--slider-w', `${sliderRect.w}px`)
	}, [sliderRect])

	return (
		<Wrap ref={wrapRef} className={className} style={style} role='radiogroup' aria-label='Segmented switch' data-disabled={disabled || undefined}>
			<Slider aria-hidden />
			{normalized.map((opt, idx) => {
				const isActive = idx === activeIndex
				return (
					<Item
						key={opt.value}
						ref={addBtnRef}
						role='radio'
						aria-checked={isActive}
						onClick={() => !disabled && onChange?.(opt.value)}
						tabIndex={isActive ? 0 : -1}
						disabled={disabled}
					>
						{opt.label}
					</Item>
				)
			})}
		</Wrap>
	)
}
